import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { articles, categories } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  useScrollToTop();

  const category = categories.find((c) => c.slug === slug);
  const [activeSlug, setActiveSlug] = useState(slug || '');

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.categorySlug === activeSlug),
    [activeSlug]
  );

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  if (!category && activeSlug) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 px-4 md:px-6 max-w-7xl mx-auto text-center py-20">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">分类未找到</h1>
          <p className="text-foreground-600 mb-6">抱歉，你查找的分类不存在。</p>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">返回首页</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory?.name || '' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                <i className={`${currentCategory?.icon || 'ri-folder-line'} text-xl w-5 h-5 flex items-center justify-center`}></i>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {currentCategory?.name}
              </h1>
            </div>
            <p className="text-sm text-foreground-600 ml-0 md:ml-[52px]">
              {currentCategory?.description}
            </p>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-2 mb-8 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setActiveSlug(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="mb-10">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mx-auto mb-4">
                  <i className="ri-article-line text-2xl w-7 h-7 flex items-center justify-center"></i>
                </div>
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}